'use client';

import React, { useEffect, useRef, useState } from 'react';
import Image from 'next/image';

function CheckIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-[#cda45e] flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>);

}

export default function AboutSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {if (entry.isIntersecting) setVisible(true);},
      { threshold: 0.1 }
    );
    if (ref?.current) observer?.observe(ref?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section
      id="about"
      ref={ref}
      className="relative py-20"
      style={{
        background: `url('https://www.gorgisairheat.com/assets/img/about-bg.jpg') center center / cover no-repeat`
      }}>
      
      {/* Dark overlay */}
      <div className="absolute inset-0 bg-[#1e1e1e]/85" />
      <div className="relative z-10 container mx-auto px-4">
        {/* Section Title */}
        <div className="section-title pb-10">
          <h2>About</h2>
          <p>Gorgis Air Conditioning and Heating Service San Diego</p>
        </div>

        {/* Intro paragraphs */}
        <div
          className={`max-w-4xl mx-auto mb-16 space-y-4 text-[#ddd] leading-relaxed transition-all duration-700 ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`
          }>
          
          <p>
            With <strong className="text-[#b5973a]">10 years of industry experience</strong>, our certified HVAC technicians bring unmatched expertise in heating, air conditioning, refrigeration systems, and both electrical and mechanical controls. Our comprehensive technical foundation — spanning plumbing, refrigeration machinery operations, and full-scope HVAC repair and maintenance — enables us to deliver solutions that are precise, reliable, and built to last.
          </p>
          <p>
            We have a proven track record of successfully managing complex HVAC demands across residential, commercial, and industrial environments. Our systematic approach to inspecting, diagnosing, and testing equipment allows us to pinpoint issues with accuracy and implement effective, long-lasting repair strategies — minimizing downtime and maximizing system performance.
          </p>
          <p>
            Our commitment to precision calibration and performance optimization has established us as a trusted name in the industry. We take pride in delivering superior craftsmanship, transparent communication, and exceptional customer service — because we believe every client deserves a comfortable, efficient environment and a team they can count on.
          </p>
        </div>

        {/* Welcome Section */}
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-20">
          <div
            className={`transition-all duration-700 delay-100 ${
            visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`
            }>
            
            <div className="about-img-wrapper relative overflow-hidden rounded-lg" style={{ border: '2px solid #b5973a', boxShadow: '0 8px 32px rgba(181,151,58,0.25)' }}>
              {/* Enhanced technician photo */}
              <Image
                src="/assets/images/Lic._1152761-1775421407526.png"
                alt="California HVAC Contractor License Certificate for Gorgis Air Conditioning and Heating, License No. 1152761"
                width={600}
                height={450}
                className="w-full object-cover"
                style={{
                  filter: 'contrast(1.08) brightness(1.05) saturate(1.15)',
                  display: 'block',
                }}
              />
              {/* Subtle gold gradient overlay at bottom */}
              <div className="absolute bottom-0 left-0 right-0 h-16 pointer-events-none" style={{
                background: 'linear-gradient(to top, rgba(181,151,58,0.18) 0%, transparent 100%)'
              }} />
              {/* Brand badge */}
              <div className="absolute bottom-3 left-3 flex items-center gap-2 bg-[#1e1e1e]/80 backdrop-blur-sm px-3 py-1.5 rounded" style={{ border: '1px solid rgba(181,151,58,0.4)' }}>
                <div className="w-1.5 h-1.5 rounded-full bg-[#b5973a]" />
                <span className="text-[#b5973a] text-xs tracking-widest uppercase font-medium">Gorgis Air · San Diego</span>
              </div>
            </div>
          </div>
          <div
            className={`transition-all duration-700 delay-200 ${
            visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`
            }>
            
            <h3 className="font-serif text-[#b5973a] text-2xl font-semibold mb-2">
              Welcome to Gorgis Air Conditioning and Heating
            </h3>
            <h3 className="font-serif text-[#b5973a] text-xl font-semibold mb-4 text-center">
              Residential &amp; Commercial HVAC Service
            </h3>
            <p className="text-[#ddd] italic mb-6 leading-relaxed">
              With warmer weather around the corner, it&apos;s a great time to consider your air conditioning needs. Contact us today for great deals. Exceptional service is our promise — we offer upfront estimates with a 100% satisfaction guarantee.
            </p>
            <ul className="check-list space-y-2">
              {[
              'Furnace Repair',
              'Furnace Installation',
              'Preventative, Maintenance and Tune-Up',
              'Air Conditioning Repair',
              'Air Conditioning Installation',
              'Air Conditioning Maintenance and Tune-Up']?.
              map((item) =>
              <li key={item} className="flex items-start gap-2 text-[#eee]">
                  <CheckIcon />
                  <span>{item}</span>
                </li>
              )}
            </ul>
          </div>
        </div>

        {/* Professional HVAC Section */}
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-20">
          <div
            className={`lg:order-2 transition-all duration-700 delay-100 ${
            visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`
            }>
            
            <div className="about-img-wrapper">
              <Image
                src="/assets/images/CA-Lic_1152761-1775421279244.png"
                alt="California HVAC Contractor License Certificate for Gorgis Air Conditioning & Heating"
                width={600}
                height={450}
                className="w-full object-cover" />
            </div>
          </div>
          <div
            className={`lg:order-1 transition-all duration-700 delay-200 ${
            visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`
            }>
            
            <h3 className="font-serif text-[#b5973a] text-xl font-semibold mb-6 leading-snug">
              Our professional HVAC service expertise covers components and complete systems for all major makes and models of:
            </h3>
            <ul className="check-list space-y-2">
              {[
              'Central air conditioners and heaters',
              'Heat pumps, Furnaces, Air handlers',
              'Ductless and split systems',
              'Zoned HVAC systems',
              'Thermostats and air filters',
              'AC compressors',
              'AC condensers',
              'Evaporator coils',
              'Swamp and evaporate coolers']?.
              map((item) =>
              <li key={item} className="flex items-start gap-2 text-[#eee]">
                  <CheckIcon />
                  <span>{item}</span>
                </li>
              )}
            </ul>
          </div>
        </div>

        {/* HVAC Services Section */}
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div
            className={`transition-all duration-700 delay-100 ${
            visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`
            }>
            
            <div className="about-img-wrapper">
              <Image
                src="/assets/images/655972528_924733383635304_8105503877129477990_n-1775417976202.png"
                alt="Gorgis Air Conditioning & Heating technician performing full HVAC inspection with company van parked outside"
                width={600}
                height={450}
                className="w-full object-cover" />
            </div>
          </div>
          <div
            className={`transition-all duration-700 delay-200 ${
            visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`
            }>
            
            <p className="text-[#ddd] italic mb-6 leading-relaxed">
              Our qualified San Diego HVAC professionals are some of the best in the industry. All employees have been drug screened and background checked to ensure safety and reliability. Call now to have your HVAC needs handled by our professional qualified staff.
            </p>
            <h4 className="font-serif text-[#b5973a] text-xl font-semibold mb-4">HVAC Services San Diego:</h4>
            <div className="grid grid-cols-2 gap-x-4">
              <ul className="check-list space-y-1">
                {[
                'AC and Furnace Repair',
                'Wall Heater Install & Repair',
                'Central AC Maintenance',
                'Furnace Tune-UP',
                'Water Heater Installation',
                'HVAC Full System Rejuvenation',
                'Ventilation and Air Service',
                'Heating and Cooling Installation',
                'Crawl Space Ventilation']?.
                map((item) =>
                <li key={item} className="flex items-start gap-1 text-[#eee] text-sm">
                    <CheckIcon />
                    <span>{item}</span>
                  </li>
                )}
              </ul>
              <ul className="check-list space-y-1">
                {[
                'Central AC Repair',
                'Ductwork Installation & Sealing',
                'Heating Pump Repair',
                'Oil to Gas Furnace Conversion',
                'Heating & Cooling Repair',
                'AC & Heating System Inspection',
                'Central AC Installation',
                'Ventilation and Ductwork']?.
                map((item) =>
                <li key={item} className="flex items-start gap-1 text-[#eee] text-sm">
                    <CheckIcon />
                    <span>{item}</span>
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>);

}